import { useIsMobile } from "@shared/hooks/useIsMobile";
import { contentSectionSupplementaryContent } from "@modules/course/access/side-bar/lesson-details/Icon";
import { useAccess } from "@templates/course/hooks/useAccess";
import { useNonAccess } from "@templates/course/hooks/useNonAccess";
import { useMemo } from "react";

const notExpandedCount = 6;

const SUPPLEMENTARY_CONTENTS = contentSectionSupplementaryContent(
  "w-5 h-5 lg:w-8 lg:h-8 text-inherit"
);

const useCourseContent = () => {
  const {
    courseData: { product },
  } = useAccess();
  const { courseContentItem } = useNonAccess();
  const isTablet = useIsMobile(1024);

  const filteredSupplemenary = useMemo(() => {
    if (!product?.supplementary_content_counts) return [];

    const { supplementary_content_counts } = product || {};

    return Object.keys(SUPPLEMENTARY_CONTENTS)
      .map((supplementaryKey) => {
        const valueKeys = Object.keys(supplementary_content_counts);
        if (!valueKeys.includes(supplementaryKey)) return null;

        const count =
          supplementary_content_counts[
            supplementaryKey as keyof typeof SUPPLEMENTARY_CONTENTS
          ];

        return count
          ? {
              key: supplementaryKey,
              value: count,
            }
          : null;
      })
      .filter(Boolean) as {
      key: string;
      value: number;
    }[];
  }, [product]);

  const filteredCourseContentItems = courseContentItem.filter((d) => d.enable);

  const courseContent = filteredCourseContentItems.map((item, index) => {
    return (
      <div
        className="flex items-center gap-2 transition-opacity duration-300"
        key={index}
      >
        <div className="w-fds-22 h-5 lg:h-8 lg:w-9 relative text-fds-brand">
          <div className="absolute top-0 left-0 z-10">
            {item.icon(isTablet ? 20 : 32)}
          </div>
          <div className="w-4 lg:w-6 h-4 lg:h-6 rounded-full bg-fds-brand-light absolute bottom-0 right-0" />
        </div>
        <p className="title-sm md:title-md-demibold text-fds-secondary">
          {item.title}
        </p>
      </div>
    );
  });

  const supplementaryContent = filteredSupplemenary.map(
    (supplementary, idx) => {
      const staticData =
        SUPPLEMENTARY_CONTENTS[
          supplementary.key as keyof typeof SUPPLEMENTARY_CONTENTS
        ];

      return (
        <div className="flex items-center gap-2" key={idx}>
          <div className="w-fds-22 h-5 lg:h-8 lg:w-9 relative text-fds-brand">
            <div className="absolute top-0 left-0 z-10">{staticData.icon}</div>
            <div className="w-4 lg:w-6 h-4 lg:h-6 rounded-full bg-fds-brand-light absolute bottom-0 right-0" />
          </div>
          <p className="title-sm md:title-md-demibold text-fds-secondary">
            {staticData.label}
          </p>
        </div>
      );
    }
  );

  const [staticItems, expandableItems] = useMemo(() => {
    const allItems = [...courseContent, ...supplementaryContent];
    return allItems.length <= 6
      ? [allItems, []]
      : [allItems.slice(0, notExpandedCount), allItems.slice(notExpandedCount)];
  }, [courseContent, supplementaryContent]);

  return { staticItems, expandableItems };
};

export default useCourseContent;
